/**
 * 2.1 §0: campañas de descuento («sin comisión») y de cashback (`promoCampaigns`). Mismo patrón que
 * las campañas de envío: el servidor (admin-updatePromoCampaign) valida con PromoCampaignSchema y
 * audita. Importes en EUR (céntimos en Firestore; aquí en euros). Interruptores en Sistema:
 * «Promoción sin comisión» y «Devolución al monedero (cashback)».
 */
import { useEffect, useState, type ReactElement } from 'react';
import { collection, getDocs, orderBy, query } from 'firebase/firestore';
import { api } from '@nuvvu/shared/api';
import { call, errorText } from '../api.js';
import { db } from '../firebase.js';
import { btn, card, field, money, muted, Notice, str, Table, td, when } from '../lib/ui.js';

type Doc = Record<string, unknown>;
type Kind = 'protection_discount' | 'cashback';

const KIND_LABEL: Record<Kind, string> = {
  protection_discount: 'Descuento en protección',
  cashback: 'Cashback',
};

interface Form {
  id: string;
  kind: Kind;
  name: string;
  active: boolean;
  club_only: boolean;
  percent: string;
  max_per_order_eur: string;
  credit_valid_days: string;
  min_item_price_eur: string;
  countries: string;
  category_ids: string;
  budget_eur: string;
  starts_at: string;
  ends_at: string;
  reason: string;
}

const localInput = (d: Date): string => {
  const p = (n: number): string => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}T${p(d.getHours())}:${p(d.getMinutes())}`;
};
const toDate = (v: unknown): Date =>
  v && typeof v === 'object' && 'toDate' in v ? (v as { toDate: () => Date }).toDate() : new Date();
const eur = (v: unknown): string =>
  String((((v as Doc | undefined)?.amount as number | undefined) ?? 0) / 100);

const blank = (kind: Kind): Form => ({
  id: '',
  kind,
  name: '',
  active: true,
  club_only: false,
  percent: kind === 'cashback' ? '5' : '100',
  max_per_order_eur: kind === 'cashback' ? '10' : '0',
  credit_valid_days: kind === 'cashback' ? '90' : '0',
  min_item_price_eur: '0',
  countries: '',
  category_ids: '',
  budget_eur: '300',
  starts_at: localInput(new Date()),
  ends_at: localInput(new Date(Date.now() + 5 * 86_400_000)),
  reason: '',
});

const fromDoc = (c: Doc): Form => ({
  id: str(c.id),
  kind: c.kind === 'cashback' ? 'cashback' : 'protection_discount',
  name: str(c.name),
  active: c.active === true,
  club_only: c.club_only === true,
  percent: String(typeof c.percent === 'number' ? c.percent : 0),
  max_per_order_eur: eur(c.max_per_order),
  credit_valid_days: String(typeof c.credit_valid_days === 'number' ? c.credit_valid_days : 0),
  min_item_price_eur: eur(c.min_item_price),
  countries: ((c.countries as string[] | undefined) ?? []).join(', '),
  category_ids: ((c.category_ids as string[] | undefined) ?? []).join(', '),
  budget_eur: eur(c.budget),
  starts_at: localInput(toDate(c.starts_at)),
  ends_at: localInput(toDate(c.ends_at)),
  reason: '',
});

const list = (s: string): string[] =>
  s
    .split(/[,\s]+/)
    .map((x) => x.trim())
    .filter(Boolean);
const cents = (s: string): number => Math.round(Number(s.replace(',', '.')) * 100);
const int = (s: string): number => Math.round(Number(s.replace(',', '.')));

function Input(props: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  type?: string;
  placeholder?: string;
}): ReactElement {
  return (
    <label style={{ display: 'grid', gap: 4, fontSize: 13 }}>
      {props.label}
      <input
        style={field}
        type={props.type ?? 'text'}
        inputMode={props.type ? undefined : 'decimal'}
        value={props.value}
        placeholder={props.placeholder}
        onChange={(e) => {
          props.onChange(e.target.value);
        }}
      />
    </label>
  );
}

export function PromoCampaignsPage(): ReactElement {
  const [rows, setRows] = useState<Doc[] | null>(null);
  const [form, setForm] = useState<Form | null>(null);
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const load = (): void => {
    getDocs(query(collection(db, 'promoCampaigns'), orderBy('created_at', 'desc')))
      .then((s) => {
        setRows(s.docs.map((d) => ({ id: d.id, ...d.data() })));
      })
      .catch(() => {
        setRows([]);
      });
  };
  useEffect(load, []);

  const set = (k: keyof Form, v: string | boolean): void => {
    setForm((f) => (f ? { ...f, [k]: v } : f));
  };
  const save = (): void => {
    if (!form) return;
    setBusy(true);
    setError(null);
    setNotice(null);
    const cashback = form.kind === 'cashback';
    const data = {
      kind: form.kind,
      name: form.name.trim(),
      active: form.active,
      club_only: form.club_only,
      percent: int(form.percent),
      max_per_order: { amount: cashback ? cents(form.max_per_order_eur) : 0, currency: 'EUR' },
      credit_valid_days: cashback ? int(form.credit_valid_days) : 0,
      min_item_price: { amount: cents(form.min_item_price_eur), currency: 'EUR' },
      countries: list(form.countries).map((c) => c.toUpperCase()),
      category_ids: list(form.category_ids),
      budget: { amount: cents(form.budget_eur), currency: 'EUR' },
      starts_at: new Date(form.starts_at).getTime(),
      ends_at: new Date(form.ends_at).getTime(),
    };
    call(api.admin.updatePromoCampaign, {
      ...(form.id ? { id: form.id } : {}),
      data,
      ...(form.reason.trim() ? { reason: form.reason.trim() } : {}),
    })
      .then((r) => {
        setNotice(`Campaña guardada (${r.id}).`);
        setForm(null);
        load();
      })
      .catch((e: unknown) => {
        setError(errorText(e));
      })
      .finally(() => {
        setBusy(false);
      });
  };

  return (
    <>
      <h1 style={{ margin: 0 }}>Campañas de descuento</h1>
      <p style={muted}>
        Black Friday y similares, pagado por nuvvu (el vendedor cobra lo mismo).{' '}
        <strong>Descuento en protección</strong>: el comprador paga ese % menos en la línea
        «Protección» (100 % = sin comisión). <strong>Cashback</strong>: al completarse el pedido
        vuelve ese % del artículo como saldo de premio (no se retira), con tope por pedido. Los
        socios del Club entran 2 días antes. Si hay varias, se aplica la de % más alto de cada tipo.
        Al agotar el presupuesto se desactiva sola. Hace falta encender su interruptor en Sistema.
      </p>
      <Notice text={notice} />
      <Notice text={error} error />
      {!rows && <p style={muted}>Cargando…</p>}
      {rows && (
        <Table
          head={[
            'Nombre',
            'Tipo',
            '%',
            'Activa',
            'Solo Club',
            'Países',
            'Presupuesto',
            'Gastado',
            'Pedidos',
            'Desde',
            'Hasta',
            '',
          ]}
        >
          {rows.map((c) => (
            <tr key={str(c.id)}>
              <td style={td}>{str(c.name)}</td>
              <td style={td}>
                {KIND_LABEL[c.kind === 'cashback' ? 'cashback' : 'protection_discount']}
              </td>
              <td style={td}>{str(c.percent)} %</td>
              <td style={td}>{c.active === true ? 'sí' : 'no'}</td>
              <td style={td}>{c.club_only === true ? 'sí' : 'no'}</td>
              <td style={td}>{((c.countries as string[] | undefined) ?? []).join(', ') || '*'}</td>
              <td style={td}>{money(c.budget)}</td>
              <td style={td}>{money(c.spent)}</td>
              <td style={td}>{str(c.orders_count)}</td>
              <td style={td}>{when(c.starts_at)}</td>
              <td style={td}>{when(c.ends_at)}</td>
              <td style={td}>
                <button
                  type="button"
                  style={btn(false)}
                  onClick={() => {
                    setForm(fromDoc(c));
                  }}
                >
                  Editar
                </button>
              </td>
            </tr>
          ))}
          {rows.length === 0 && (
            <tr>
              <td style={td} colSpan={12}>
                Sin campañas.
              </td>
            </tr>
          )}
        </Table>
      )}
      {!form && (
        <div style={{ display: 'flex', gap: 8 }}>
          <button
            type="button"
            style={btn(true)}
            onClick={() => {
              setForm(blank('protection_discount'));
            }}
          >
            Nueva «sin comisión»
          </button>
          <button
            type="button"
            style={btn(true)}
            onClick={() => {
              setForm(blank('cashback'));
            }}
          >
            Nuevo cashback
          </button>
        </div>
      )}
      {form && (
        <form
          style={{ ...card, display: 'grid', gap: 10, maxWidth: 560 }}
          onSubmit={(e) => {
            e.preventDefault();
            save();
          }}
        >
          <strong>
            {form.id ? `Editar ${form.id}` : 'Nueva campaña'} · {KIND_LABEL[form.kind]}
          </strong>
          <Input
            label="Nombre (solo para el admin)"
            value={form.name}
            onChange={(v) => {
              set('name', v);
            }}
          />
          <label style={{ display: 'flex', gap: 8, alignItems: 'center', fontSize: 13 }}>
            <input
              type="checkbox"
              checked={form.active}
              onChange={(e) => {
                set('active', e.target.checked);
              }}
            />
            Activa
          </label>
          <label style={{ display: 'flex', gap: 8, alignItems: 'center', fontSize: 13 }}>
            <input
              type="checkbox"
              checked={form.club_only}
              onChange={(e) => {
                set('club_only', e.target.checked);
              }}
            />
            Solo socios del Club nuvvu
          </label>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
            <Input
              label={
                form.kind === 'cashback'
                  ? 'Cashback (% del artículo)'
                  : 'Descuento en la protección (%; 100 = sin comisión)'
              }
              value={form.percent}
              onChange={(v) => {
                set('percent', v);
              }}
            />
            <Input
              label="Compra mínima (€ del artículo; 0 = cualquiera)"
              value={form.min_item_price_eur}
              onChange={(v) => {
                set('min_item_price_eur', v);
              }}
            />
            {form.kind === 'cashback' && (
              <>
                <Input
                  label="Tope por pedido (€; 0 = sin tope)"
                  value={form.max_per_order_eur}
                  onChange={(v) => {
                    set('max_per_order_eur', v);
                  }}
                />
                <Input
                  label="Días que dura el saldo (0 = no caduca)"
                  value={form.credit_valid_days}
                  onChange={(v) => {
                    set('credit_valid_days', v);
                  }}
                />
              </>
            )}
          </div>
          <Input
            label="Países de destino (ISO-2, separados por comas; vacío = todos)"
            value={form.countries}
            placeholder="ES, PT, HU"
            onChange={(v) => {
              set('countries', v);
            }}
          />
          <Input
            label="Ids de categoría (separados por comas; vacío = todas)"
            value={form.category_ids}
            placeholder="women_shoes, kids"
            onChange={(v) => {
              set('category_ids', v);
            }}
          />
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
            <Input
              label="Presupuesto total (€; lo que asume nuvvu)"
              value={form.budget_eur}
              onChange={(v) => {
                set('budget_eur', v);
              }}
            />
            <span />
            <Input
              label="Desde (para todos)"
              type="datetime-local"
              value={form.starts_at}
              onChange={(v) => {
                set('starts_at', v);
              }}
            />
            <Input
              label="Hasta"
              type="datetime-local"
              value={form.ends_at}
              onChange={(v) => {
                set('ends_at', v);
              }}
            />
          </div>
          <Input
            label="Motivo (auditoría)"
            value={form.reason}
            onChange={(v) => {
              set('reason', v);
            }}
          />
          <div style={{ display: 'flex', gap: 8 }}>
            <button type="submit" style={btn(true)} disabled={busy}>
              Guardar
            </button>
            <button
              type="button"
              style={btn(false)}
              onClick={() => {
                setForm(null);
              }}
            >
              Cancelar
            </button>
          </div>
        </form>
      )}
    </>
  );
}
